// src/pages/ImportarHistorico.js
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import PlatformBackButton from '../components/PlatformBackButton';
import { db } from '../firebase';
import { collection, getDocs, doc, writeBatch, serverTimestamp } from 'firebase/firestore';
import * as pdfjsLib from 'pdfjs-dist';
import * as XLSX from 'xlsx';
import {
    FaFileInvoice, FaCloudUploadAlt, FaDownload, FaCheckCircle, FaExclamationTriangle,
    FaExclamationCircle, FaChartBar, FaCalendarAlt, FaDollarSign, FaHome, FaInfoCircle,
    FaArrowRight, FaTrash, FaListAlt
} from 'react-icons/fa';
import './ImportarHistorico.css';

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const parseSpanishDate = (str) => {
    if (!str) return new Date();
    if (str instanceof Date) return str;
    if (str.toDate && typeof str.toDate === 'function') return str.toDate();

    const strClean = String(str).trim();

    // Formato DD/MM/YYYY
    const slashParts = strClean.split('/');
    if (slashParts.length === 3) {
        const day = parseInt(slashParts[0], 10);
        const month = parseInt(slashParts[1], 10) - 1;
        const year = parseInt(slashParts[2], 10);
        if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
            return new Date(year, month, day, 12, 0, 0);
        }
    }

    // Formato 01 nov. 2024 / 01 nov 2024
    const monthsMap = {
        ene: 0, feb: 1, mar: 2, abr: 3, may: 4, jun: 5,
        jul: 6, ago: 7, sep: 8, oct: 9, nov: 10, dic: 11
    };

    const match = strClean.toLowerCase().match(/(\d{1,2})\s+([a-z]{3})\.?\s+(\d{4})/);
    if (match) {
        const day = parseInt(match[1], 10);
        const monthStr = match[2];
        const year = parseInt(match[3], 10);
        const month = monthsMap[monthStr];
        if (month !== undefined && !isNaN(day) && !isNaN(year)) {
            return new Date(year, month, day, 12, 0, 0);
        }
    }

    const fallbackDate = new Date(strClean);
    return isNaN(fallbackDate.getTime()) ? new Date() : fallbackDate;
};

const ImportarHistorico = () => {
    const { userData, fraccionamientoId } = useAuth();
    const { showNotification } = useNotification();
    const activeFraccId = fraccionamientoId || userData?.fraccionamientoId;

    const [casasExistentes, setCasasExistentes] = useState([]);
    const [loadingCasas, setLoadingCasas] = useState(true);
    const [file, setFile] = useState(null);
    const [parsing, setParsing] = useState(false);
    const [importing, setImporting] = useState(false);

    // Datos procesados del archivo
    const [extractedHeader, setExtractedHeader] = useState({
        unidad: '',
        contacto: '',
        saldoActual: 0,
        saldoInicial: 0
    });
    const [extractedRows, setExtractedRows] = useState([]);
    const [selectedCasaId, setSelectedCasaId] = useState('');
    const [casaMatchStatus, setCasaMatchStatus] = useState(null); // 'matched' | 'not_found'

    const fileInputRef = useRef(null);

    // Cargar la lista de casas del fraccionamiento para validación estricta
    useEffect(() => {
        let isMounted = true;
        const fetchCasas = async () => {
            if (!activeFraccId) {
                setLoadingCasas(false);
                return;
            }
            try {
                const casasRef = collection(db, 'fraccionamientos', activeFraccId, 'casas');
                const snap = await getDocs(casasRef);
                const list = snap.docs.map(d => ({
                    id: d.id,
                    ...d.data()
                }));
                if (isMounted) {
                    setCasasExistentes(list);
                    setLoadingCasas(false);
                }
            } catch (err) {
                console.error('ImportarHistorico: Error cargando casas:', err);
                if (isMounted) setLoadingCasas(false);
            }
        };

        fetchCasas();
        return () => { isMounted = false; };
    }, [activeFraccId]);

    // Función para descargar la Plantilla Excel estándar
    const handleDescargarPlantilla = () => {
        try {
            const dataTemplate = [
                {
                    'Unidad / Casa': 'T1-Local 01',
                    'Contacto Principal': 'JUAN PEREZ GONZALEZ',
                    'Fecha': '01/11/2024',
                    'Tipo': 'Cuota',
                    'Concepto': 'Cuota mantenimiento Mes de Noviembre 2024',
                    'Cargo': 1300.00,
                    'Abono': 0.00,
                    'Estado': 'Pagada'
                },
                {
                    'Unidad / Casa': 'T1-Local 01',
                    'Contacto Principal': 'JUAN PEREZ GONZALEZ',
                    'Fecha': '02/11/2024',
                    'Tipo': 'Pago',
                    'Concepto': 'Cuota mantenimiento Mes de Noviembre 2024',
                    'Cargo': 0.00,
                    'Abono': 1300.00,
                    'Estado': 'Aplicado'
                },
                {
                    'Unidad / Casa': 'T1-Local 01',
                    'Contacto Principal': 'JUAN PEREZ GONZALEZ',
                    'Fecha': '01/04/2026',
                    'Tipo': 'Cuota',
                    'Concepto': 'Cuota mantenimiento Mes de Abril 2026',
                    'Cargo': 1300.00,
                    'Abono': 0.00,
                    'Estado': 'No pagada'
                }
            ];

            const worksheet = XLSX.utils.json_to_sheet(dataTemplate);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Historico_Plantilla');

            // Ajustar ancho de columnas
            worksheet['!cols'] = [
                { wch: 18 },
                { wch: 28 },
                { wch: 14 },
                { wch: 12 },
                { wch: 42 },
                { wch: 14 },
                { wch: 14 },
                { wch: 16 }
            ];

            XLSX.writeFile(workbook, 'Plantilla_Estado_Cuenta_Historico.xlsx');
            showNotification('Plantilla Excel descargada exitosamente.', 'success');
        } catch (err) {
            console.error('Error al descargar plantilla:', err);
            showNotification('No se pudo generar la plantilla Excel.', 'error');
        }
    };

    // Parser de Archivo PDF o Excel
    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;

        setFile(selectedFile);
        parseFile(selectedFile);
    };

    const parseFile = async (fileToParse) => {
        setParsing(true);
        setExtractedRows([]);
        setExtractedHeader({ unidad: '', contacto: '', saldoActual: 0, saldoInicial: 0 });
        setSelectedCasaId('');
        setCasaMatchStatus(null);

        const fileName = fileToParse.name.toLowerCase();

        try {
            if (fileName.endsWith('.pdf')) {
                await parsePdfFile(fileToParse);
            } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
                await parseExcelFile(fileToParse);
            } else {
                showNotification('Formato no soportado. Sube un archivo PDF o Excel (.xlsx, .xls)', 'error');
                setParsing(false);
            }
        } catch (err) {
            console.error('Error procesando archivo:', err);
            showNotification('Ocurrió un error al leer la estructura del archivo.', 'error');
            setParsing(false);
        }
    };

    // Parser específico para PDF
    const parsePdfFile = async (pdfFile) => {
        const arrayBuffer = await pdfFile.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer, useSystemFonts: true, disableFontFace: true }).promise;

        let fullTextItems = [];
        for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const content = await page.getTextContent();
            const pageItems = content.items.map(item => item.str.trim()).filter(Boolean);
            fullTextItems = fullTextItems.concat(pageItems);
        }

        const fullString = fullTextItems.join(' ');

        // Extraer Unidad / Casa
        let unidadDetectada = '';
        const unidadMatch = fullString.match(/Unidad:\s*([^\s]+(?:\s+[^\s]+)*?)(?=\s+Contacto|\s+Saldo|\s+Emitido|$)/i) ||
                            fullString.match(/Unidad:\s*([^\s|]+)/i);
        if (unidadMatch && unidadMatch[1]) {
            unidadDetectada = unidadMatch[1].trim();
        }

        // Extraer Contacto
        let contactoDetectado = '';
        const contactoMatch = fullString.match(/Contacto principal:\s*([^\s]+(?:\s+[^\s]+)*?)(?=\s+Saldo|\s+Fecha|$)/i);
        if (contactoMatch && contactoMatch[1]) {
            contactoDetectado = contactoMatch[1].trim();
        }

        // Extraer Saldo Inicial
        let saldoInicialVal = 0;
        const saldoInicialMatch = fullString.match(/Saldo inicial:\s*\$?\s*([\d,]+\.?\d*)/i);
        if (saldoInicialMatch && saldoInicialMatch[1]) {
            saldoInicialVal = parseFloat(saldoInicialMatch[1].replace(/,/g, '')) || 0;
        }

        // Extraer Saldo Actual
        let saldoActualVal = 0;
        const saldoActualMatch = fullString.match(/Saldo actual:\s*\$?\s*([\d,]+\.?\d*)/i);
        if (saldoActualMatch && saldoActualMatch[1]) {
            saldoActualVal = parseFloat(saldoActualMatch[1].replace(/,/g, '')) || 0;
        }

        // Extraer Filas del Historial
        // Patrón de fecha común: 01 nov. 2024, 02/11/2024, 01-11-2024, 01 ene 2025
        const rows = [];
        const regexFila = /((?:\d{2}\s+(?:ene|feb|mar|abr|may|jun|jul|ago|sep|oct|nov|dic)\.?\s+\d{4})|(?:\d{2}\/\d{2}\/\d{4}))\s+(Cuota|Pago|Descuento|Ajuste|Nota de crédito|Nota de credito|Recargo|Interés|Interes|Abono|Cargo|Penalidad|Multa|Sanción|Sancion)\s+(.*?)\s+\$?\s*([\d,]+\.\d{2})\s+(Pagada|Pagado|Aplicado|Aplicado parcial|No pagada|No pagado|No aplicado|Cancelado|Pendiente)/gi;

        let match;
        let idCounter = 1;

        while ((match = regexFila.exec(fullString)) !== null) {
            const fechaStr = match[1];
            const tipoStr = match[2];
            const conceptoStr = match[3];
            const montoVal = parseFloat(match[4].replace(/,/g, '')) || 0;
            const estadoStr = match[5];

            const tipoLower = tipoStr.toLowerCase();
            const esCargo = tipoLower.includes('cuota') || tipoLower.includes('recargo') || tipoLower.includes('interés') || tipoLower.includes('interes') || tipoLower.includes('cargo') || tipoLower.includes('penalidad') || tipoLower.includes('multa') || tipoLower.includes('sanción') || tipoLower.includes('sancion');
            const cargo = esCargo ? montoVal : 0;
            const abono = !esCargo ? montoVal : 0;

            rows.push({
                id: idCounter++,
                fecha: fechaStr,
                fechaObj: parseSpanishDate(fechaStr),
                tipo: tipoStr,
                concepto: conceptoStr,
                cargo,
                abono,
                estado: estadoStr
            });
        }

        // Si existe un saldo inicial de adeudo, agregarlo al inicio como primer cargo
        if (saldoInicialVal > 0) {
            const fechaInicialStr = rows.length > 0 ? rows[0].fecha : '01/01/2024';
            const fechaInicialObj = rows.length > 0 ? rows[0].fechaObj : new Date();
            rows.unshift({
                id: 0,
                fecha: fechaInicialStr,
                fechaObj: fechaInicialObj,
                tipo: 'Cuota',
                concepto: 'Saldo inicial - Adeudo administración anterior',
                cargo: saldoInicialVal,
                abono: 0,
                estado: 'No pagada'
            });
        }

        setExtractedHeader({
            unidad: unidadDetectada,
            contacto: contactoDetectado,
            saldoActual: saldoActualVal,
            saldoInicial: saldoInicialVal
        });

        setExtractedRows(rows);

        // Validar coincidencia de casa
        matchCasaToDatabase(unidadDetectada);
        setParsing(false);
    };

    // Parser específico para Excel
    const parseExcelFile = async (excelFile) => {
        const data = await excelFile.arrayBuffer();
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonRows = XLSX.utils.sheet_to_json(worksheet);

        if (!jsonRows || jsonRows.length === 0) {
            showNotification('El archivo Excel está vacío.', 'warning');
            setParsing(false);
            return;
        }

        const firstRow = jsonRows[0];
        const unidadDetectada = String(firstRow['Unidad / Casa'] || firstRow['Unidad'] || firstRow['Casa'] || '').trim();
        const contactoDetectado = String(firstRow['Contacto Principal'] || firstRow['Contacto'] || firstRow['Nombre'] || '').trim();
        const saldoInicialVal = parseFloat(firstRow['Saldo Inicial'] || firstRow['Saldo inicial'] || firstRow['SaldoInicial'] || 0) || 0;

        const rows = [];
        let idCounter = 1;

        jsonRows.forEach((r) => {
            const rawFecha = r['Fecha'] || r['FECHA'] || '';
            let fecha = '';
            let fechaObj = null;

            if (rawFecha) {
                const parsedNum = Number(rawFecha);
                if (!isNaN(parsedNum) && parsedNum > 40000 && parsedNum < 60000) {
                    // Es un número de serie de Excel (rango aproximado del año 2009 al 2064)
                    fechaObj = new Date((parsedNum - 25569) * 86400 * 1000);
                    const dia = String(fechaObj.getDate()).padStart(2, '0');
                    const mes = String(fechaObj.getMonth() + 1).padStart(2, '0');
                    const anio = fechaObj.getFullYear();
                    fecha = `${dia}/${mes}/${anio}`;
                } else {
                    fecha = String(rawFecha).trim();
                }
            }

            const tipo = String(r['Tipo'] || r['TIPO'] || (r['Cargo'] > 0 ? 'Cuota' : 'Pago')).trim();
            const concepto = String(r['Concepto'] || r['CONCEPTO'] || '').trim();
            const cargo = parseFloat(r['Cargo'] || r['CARGO'] || 0) || 0;
            const abono = parseFloat(r['Abono'] || r['ABONO'] || 0) || 0;
            const estado = String(r['Estado'] || r['ESTADO'] || (abono > 0 ? 'Aplicado' : 'No pagada')).trim();

            if (fecha && concepto) {
                rows.push({
                    id: idCounter++,
                    fecha,
                    fechaObj,
                    tipo,
                    concepto,
                    cargo,
                    abono,
                    estado
                });
            }
        });

        if (saldoInicialVal > 0) {
            const fechaInicialStr = rows.length > 0 ? rows[0].fecha : '01/01/2024';
            rows.unshift({
                id: 0,
                fecha: fechaInicialStr,
                tipo: 'Cuota',
                concepto: 'Saldo inicial - Adeudo administración anterior',
                cargo: saldoInicialVal,
                abono: 0,
                estado: 'No pagada'
            });
        }

        setExtractedHeader({
            unidad: unidadDetectada,
            contacto: contactoDetectado,
            saldoActual: 0,
            saldoInicial: saldoInicialVal
        });

        setExtractedRows(rows);
        matchCasaToDatabase(unidadDetectada);
        setParsing(false);
    };

    // Comparar Unidad/Casa con la base de datos
    const matchCasaToDatabase = (unidadBuscada) => {
        if (!unidadBuscada || casasExistentes.length === 0) {
            setCasaMatchStatus('not_found');
            return;
        }

        const searchNorm = unidadBuscada.toLowerCase().replace(/[\s\-_]/g, '');
        const matched = casasExistentes.find((c) => {
            const numNorm = String(c.numero || c.numeroCasa || '').toLowerCase().replace(/[\s\-_]/g, '');
            const deptoNorm = String(c.nombre_departamento || '').toLowerCase().replace(/[\s\-_]/g, '');
            const idNorm = String(c.id || '').toLowerCase().replace(/[\s\-_]/g, '');
            return numNorm === searchNorm || deptoNorm === searchNorm || idNorm === searchNorm ||
                   (searchNorm.includes(numNorm) && numNorm.length > 2);
        });

        if (matched) {
            setSelectedCasaId(matched.id);
            setCasaMatchStatus('matched');
        } else {
            setSelectedCasaId('');
            setCasaMatchStatus('not_found');
        }
    };

    // Calcular Totales de la Carga
    const totalCargos = extractedRows.reduce((sum, r) => sum + r.cargo, 0);
    const totalAbonos = extractedRows.reduce((sum, r) => sum + r.abono, 0);
    const saldoPendienteFinal = totalCargos - totalAbonos;

    // Reporte Analítico por Año (Generado Dinámicamente)
    const reporteAnual = React.useMemo(() => {
        if (extractedRows.length === 0) return null;

        const porAño = {};

        extractedRows.forEach((r) => {
            // Extraer año de la fecha (ej. "01 nov. 2024" -> "2024", "01/11/2024" -> "2024")
            const yearMatch = r.fecha.match(/\b(20\d{2})\b/);
            const year = yearMatch ? yearMatch[1] : 'Año Sin Identificar';

            if (!porAño[year]) {
                porAño[year] = {
                    year,
                    cuotasMantenimiento: 0,
                    cuotasExtraordinarias: 0,
                    otrosCargos: 0,
                    totalAbonos: 0,
                    montoCuotaMantenimientoDetectado: null,
                    conteoMantenimiento: 0
                };
            }

            const conceptoLower = r.concepto.toLowerCase();
            if (conceptoLower.includes('mantenimiento')) {
                porAño[year].cuotasMantenimiento += r.cargo;
                if (r.cargo > 0) {
                    porAño[year].montoCuotaMantenimientoDetectado = r.cargo;
                    porAño[year].conteoMantenimiento++;
                }
            } else if (conceptoLower.includes('extraordinaria') || conceptoLower.includes('extra')) {
                porAño[year].cuotasExtraordinarias += r.cargo;
            } else {
                porAño[year].otrosCargos += r.cargo;
            }

            porAño[year].totalAbonos += r.abono;
        });

        const sortedYears = Object.keys(porAño).sort();
        
        // Detectar variaciones de cuota
        const desglose = sortedYears.map((yr, idx) => {
            const dataYr = porAño[yr];
            let variacion = 'Sin cambios';
            if (idx > 0) {
                const prevYr = sortedYears[idx - 1];
                const prevMonto = porAño[prevYr].montoCuotaMantenimientoDetectado;
                const currMonto = dataYr.montoCuotaMantenimientoDetectado;
                if (prevMonto && currMonto) {
                    if (currMonto > prevMonto) variacion = `Incrementó (+$${(currMonto - prevMonto).toFixed(2)})`;
                    else if (currMonto < prevMonto) variacion = `Disminuyó (-$${(prevMonto - currMonto).toFixed(2)})`;
                }
            }
            return {
                ...dataYr,
                variacion
            };
        });

        return desglose;
    }, [extractedRows]);

    // Confirmar e Importar a Firestore
    const handleConfirmarImportacion = async () => {
        if (!selectedCasaId) {
            showNotification('Debes seleccionar o vincular una casa registrada antes de guardar.', 'error');
            return;
        }

        if (extractedRows.length === 0) {
            showNotification('No hay filas válidas para importar.', 'warning');
            return;
        }

        setImporting(true);

        try {
            const batch = writeBatch(db);
            const casaRef = doc(db, 'fraccionamientos', activeFraccId, 'casas', selectedCasaId);
            const historialColl = collection(db, 'fraccionamientos', activeFraccId, 'casas', selectedCasaId, 'historial_pagos');
            const mesesPendientesColl = collection(db, 'fraccionamientos', activeFraccId, 'casas', selectedCasaId, 'meses_pendientes');
            const mesesPendientesCamelColl = collection(db, 'fraccionamientos', activeFraccId, 'casas', selectedCasaId, 'mesesPendientes');

            let countNoPagadas = 0;

            extractedRows.forEach((r) => {
                const dateObj = r.fechaObj || parseSpanishDate(r.fecha);
                // 1. Guardar cada movimiento en historial_pagos
                const histRef = doc(historialColl);
                batch.set(histRef, {
                    fecha: dateObj,
                    fechaString: r.fecha,
                    ordenPDF: r.id,
                    tipo: r.tipo,
                    concepto: r.concepto,
                    cargo: r.cargo,
                    abono: r.abono,
                    monto: r.cargo > 0 ? r.cargo : r.abono,
                    estado: r.estado,
                    metodoPago: (r.tipo === 'Pago' || r.tipo === 'Descuento' || r.tipo === 'Ajuste' || r.abono > 0) ? 'Importación Histórica' : 'Generación Automática',
                    referencia: 'HISTORICO_IMPORTADO',
                    creadoEn: serverTimestamp()
                });

                // 2. Si es una cuota o cargo No pagado o con adeudo, registrar en meses_pendientes y mesesPendientes
                const estadoLower = r.estado.toLowerCase();
                if (r.cargo > 0 && (estadoLower.includes('no pagada') || estadoLower.includes('parcial') || estadoLower.includes('con adeudo'))) {
                    countNoPagadas++;
                    
                    // Extraer año y mes del objeto de fecha para construir el ID único
                    const anio = dateObj.getFullYear();
                    const mes = String(dateObj.getMonth() + 1).padStart(2, '0');
                    const conceptoLimpio = String(r.concepto).replace(/\s+/g, '');
                    
                    const esExtraordinaria = r.concepto.toLowerCase().includes('extra') || r.tipo.toLowerCase().includes('extra');
                    
                    // Generar ID del documento: EXT-[Concepto]-[AAAA]-[MM] para extraordinarias o [AAAA]-[MM] para ordinarias
                    const mesDocId = esExtraordinaria 
                        ? `EXT-${conceptoLimpio}-${anio}-${mes}` 
                        : `${anio}-${mes}`;
                    
                    const mesDocRef = doc(mesesPendientesColl, mesDocId);
                    const payloadAdeudo = {
                        concepto: r.concepto,
                        fecha: r.fecha,
                        monto: r.cargo,
                        montoOriginal: r.cargo,
                        montoPagado: r.abono,
                        montoPendiente: r.cargo - r.abono,
                        estado: 'pendiente',
                        tipo: esExtraordinaria ? 'cuota_extraordinaria' : 'cuota_mantenimiento',
                        origen: 'importacion_historico',
                        creadoEn: serverTimestamp(),
                        año: anio,
                        anio: anio,
                        mes: Number(mes),
                        mesId: `${anio}-${mes}`,
                        esExtraordinaria: esExtraordinaria
                    };
                    batch.set(mesDocRef, payloadAdeudo);

                    const mesCamelRef = doc(mesesPendientesCamelColl, mesDocId);
                    batch.set(mesCamelRef, payloadAdeudo);
                }
            });

            // Actualizar adeudo acumulado de la casa (adeudo y adeudoCapital)
            const finalAdeudoVal = saldoPendienteFinal > 0 ? saldoPendienteFinal : 0;
            batch.update(casaRef, {
                adeudo: finalAdeudoVal,
                adeudoCapital: finalAdeudoVal,
                saldoFavor: saldoPendienteFinal < 0 ? Math.abs(saldoPendienteFinal) : 0,
                ultimoHistoricoImportado: serverTimestamp()
            });

            await batch.commit();

            showNotification(
                `Histórico importado exitosamente. Se guardaron ${extractedRows.length} movimientos y ${countNoPagadas} meses pendientes de adeudo.`,
                'success'
            );

            setImporting(false);
            setFile(null);
            setExtractedRows([]);
            setExtractedHeader({ unidad: '', contacto: '', saldoActual: 0, saldoInicial: 0 });
            setSelectedCasaId('');
            setCasaMatchStatus(null);
        } catch (err) {
            console.error('Error al guardar histórico en Firestore:', err);
            showNotification('Error al guardar los registros en la base de datos.', 'error');
            setImporting(false);
        }
    };

    return (
        <div className="importar-historico-page">
            <PlatformBackButton defaultPath="/configuraciones" />

            <div className="importar-historico-container">
                {/* Encabezado Principal */}
                <header className="importar-historico-header">
                    <div className="header-icon-badge">
                        <FaFileInvoice />
                    </div>
                    <div>
                        <h1>Importación de Histórico de Residentes</h1>
                        <p>Carga estados de cuenta en PDF o Excel para registrar automáticamente el historial de pagos y adeudos pendientes por casa.</p>
                    </div>
                </header>

                {/* Barra de Acciones: Descarga de Plantilla y Subida */}
                <div className="importar-historico-card glass-panel">
                    <div className="actions-toolbar">
                        <div className="toolbar-info">
                            <FaInfoCircle className="info-icon" />
                            <span>Puedes cargar archivos PDF del sistema (ej. <code>local 1.pdf</code>) o descargar la plantilla Excel estandarizada.</span>
                        </div>
                        <button
                            type="button"
                            className="btn-download-template"
                            onClick={handleDescargarPlantilla}
                        >
                            <FaDownload />
                            <span>Descargar Plantilla Excel</span>
                        </button>
                    </div>

                    {/* Zona de Drop/Upload */}
                    <div 
                        className="upload-dropzone"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <FaCloudUploadAlt className="upload-cloud-icon" />
                        <h3>Haz clic o arrastra tu archivo aquí</h3>
                        <p>Soporta formatos <strong>PDF</strong>, <strong>.xlsx</strong> y <strong>.xls</strong></p>
                        {file && (
                            <div className="file-badge">
                                <FaFileInvoice />
                                <span>{file.name} ({(file.size / 1024).toFixed(1)} KB)</span>
                            </div>
                        )}
                        <input
                            type="file"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            accept=".pdf,.xlsx,.xls"
                            onChange={handleFileChange}
                        />
                    </div>
                </div>

                {parsing && (
                    <div className="parsing-loader-card">
                        <div className="loader-spinner"></div>
                        <p>Analizando y extrayendo filas del documento...</p>
                    </div>
                )}

                {/* Resultado de la Extracción */}
                {extractedRows.length > 0 && (
                    <div className="extracted-data-section">
                        {/* Tarjeta de Encabezado y Validación de Casa */}
                        <div className="extracted-header-card glass-panel">
                            <div className="header-card-top">
                                <div className="unit-info">
                                    <FaHome className="unit-icon" />
                                    <div>
                                        <h3>Unidad Detectada: <strong>{extractedHeader.unidad || 'Sin especificar'}</strong></h3>
                                        <p>Contacto Principal: {extractedHeader.contacto || 'No indicado'}</p>
                                        {extractedHeader.saldoInicial > 0 && (
                                            <p style={{ color: '#d32f2f', fontWeight: 'bold', marginTop: '4px' }}>
                                                Saldo Inicial de Adeudo Detectado: ${extractedHeader.saldoInicial.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Selección / Validación Estricta de Casa */}
                                <div className="casa-selector-wrapper">
                                    <label>Vincular a Casa Registrada:</label>
                                    <select
                                        value={selectedCasaId}
                                        onChange={(e) => {
                                            setSelectedCasaId(e.target.value);
                                            setCasaMatchStatus(e.target.value ? 'matched' : 'not_found');
                                        }}
                                        className={casaMatchStatus === 'not_found' ? 'select-warning' : 'select-success'}
                                    >
                                        <option value="">-- Seleccionar Casa del Fraccionamiento --</option>
                                        {casasExistentes.map((c) => (
                                            <option key={c.id} value={c.id}>
                                                {c.numero || c.nombre_departamento || `Casa ${c.id}`} ({c.nombre_residente || c.propietario || 'Sin residente'})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Alertas de Coincidencia */}
                            {casaMatchStatus === 'matched' && (
                                <div className="alert-badge success">
                                    <FaCheckCircle />
                                    <span>Unidad vinculada correctamente con la base de datos del fraccionamiento.</span>
                                </div>
                            )}

                            {casaMatchStatus === 'not_found' && (
                                <div className="alert-badge error">
                                    <FaExclamationTriangle />
                                    <span>Atención: La unidad "{extractedHeader.unidad}" no coincide con ninguna casa registrada. Selecciona manualmente la propiedad arriba para poder guardar.</span>
                                </div>
                            )}
                        </div>

                        {/* Tabla de Vista Previa del Historial */}
                        <div className="extracted-rows-card glass-panel">
                            <div className="rows-card-header">
                                <div className="title-row">
                                    <FaListAlt />
                                    <h3>Vista Previa del Historial de Pagos</h3>
                                </div>
                                <span className="badge-count">{extractedRows.length} movimientos detectados</span>
                            </div>

                            <div className="table-responsive-wrapper">
                                <table className="table-historico-preview">
                                    <thead>
                                        <tr>
                                            <th>FECHA</th>
                                            <th>TIPO</th>
                                            <th>CONCEPTO</th>
                                            <th>CARGO</th>
                                            <th>ABONO</th>
                                            <th>ESTADO</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {extractedRows.map((r) => {
                                            const estLower = r.estado.toLowerCase();
                                            let badgeClass = 'badge-no-pagada';
                                            if (estLower.includes('pagada') || estLower.includes('aplicado') && !estLower.includes('parcial') && !estLower.includes('no')) {
                                                badgeClass = 'badge-pagada';
                                            } else if (estLower.includes('parcial')) {
                                                badgeClass = 'badge-parcial';
                                            }

                                            return (
                                                <tr key={r.id}>
                                                    <td>{r.fecha}</td>
                                                    <td>
                                                        <span className={`tipo-pill ${r.tipo.toLowerCase()}`}>{r.tipo}</span>
                                                    </td>
                                                    <td>{r.concepto}</td>
                                                    <td className="monto-cargo">{r.cargo > 0 ? `$${r.cargo.toFixed(2)}` : '—'}</td>
                                                    <td className="monto-abono">{r.abono > 0 ? `$${r.abono.toFixed(2)}` : '—'}</td>
                                                    <td>
                                                        <span className={`status-badge ${badgeClass}`}>{r.estado}</span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    {/* Fila de Totales al pie de la tabla */}
                                    <tfoot>
                                        <tr className="tfoot-totals-row">
                                            <td colSpan="3" className="text-right"><strong>SUMATORIA DE TOTALES:</strong></td>
                                            <td className="monto-cargo"><strong>$ {totalCargos.toFixed(2)}</strong></td>
                                            <td className="monto-abono"><strong>$ {totalAbonos.toFixed(2)}</strong></td>
                                            <td className="saldo-pendiente-total">
                                                <strong>Pendiente: ${saldoPendienteFinal.toFixed(2)}</strong>
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>

                            {/* Botón de Confirmación */}
                            <div className="rows-card-footer">
                                <button
                                    type="button"
                                    className="btn-confirmar-historico"
                                    disabled={importing || !selectedCasaId}
                                    onClick={handleConfirmarImportacion}
                                >
                                    <span>{importing ? 'Guardando en Firestore...' : 'Confirmar e Importar Histórico'}</span>
                                    <FaArrowRight />
                                </button>
                            </div>
                        </div>

                        {/* Reporte Analítico de Cuotas por Año al Pie */}
                        {reporteAnual && (
                            <div className="reporte-analitico-card glass-panel">
                                <div className="reporte-header">
                                    <FaChartBar className="chart-icon" />
                                    <div>
                                        <h3>Reporte Analítico: Variación de Cuotas por Año</h3>
                                        <p>Análisis de las cuotas de mantenimiento y extraordinarias cobradas por año en este estado de cuenta.</p>
                                    </div>
                                </div>

                                <div className="reporte-years-grid">
                                    {reporteAnual.map((item) => (
                                        <div key={item.year} className="year-analytics-card">
                                            <div className="year-card-header">
                                                <FaCalendarAlt />
                                                <h4>Año {item.year}</h4>
                                            </div>
                                            <div className="year-card-body">
                                                <div className="stat-row">
                                                    <span>Cuota Mantenimiento Detectada:</span>
                                                    <strong>{item.montoCuotaMantenimientoDetectado ? `$${item.montoCuotaMantenimientoDetectado.toFixed(2)}` : 'N/A'}</strong>
                                                </div>
                                                <div className="stat-row">
                                                    <span>Variación vs Año Anterior:</span>
                                                    <span className="variacion-tag">{item.variacion}</span>
                                                </div>
                                                <div className="stat-row">
                                                    <span>Total Mantenimiento:</span>
                                                    <strong>$ {item.cuotasMantenimiento.toFixed(2)}</strong>
                                                </div>
                                                <div className="stat-row">
                                                    <span>Total Extraordinarias:</span>
                                                    <strong>$ {item.cuotasExtraordinarias.toFixed(2)}</strong>
                                                </div>
                                                <div className="stat-row">
                                                    <span>Otros Cargos:</span>
                                                    <strong>$ {item.otrosCargos.toFixed(2)}</strong>
                                                </div>
                                                <div className="stat-row total-abonos">
                                                    <span>Abonos Pagados:</span>
                                                    <strong>$ {item.totalAbonos.toFixed(2)}</strong>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ImportarHistorico;
